import { Navigate } from "react-router-dom";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, CancelToken } from "axios";
import { getCookie, setCookie } from "react-use-cookie";
import { clearSessionToken, readSessionFallback } from "./sessionToken";
import { isAuthHeaderBlocked, isSameOriginApi, markAuthHeaderBlocked } from "./authHeaderFallback";
import { rememberReturnTo } from "./returnTo";
import { isPortalHost } from "../pages/Portal/portalHost";
import useLoginToken from "../store/login.store";
import { IDefaultResponse, IErrorResponse, TPremiseResponse } from "./interface/IDefaults";
import { DTE_2FA_REQUIRED, pedirTwoFactorDeNovo } from "./twoFactorRequired";

/**
 * GEN-3199: janela que separa "bloqueio local" de "timeout de rede". O timeout
 * das instâncias é 300s; um bloqueio de agente falha em milissegundos.
 */
const FAST_FAILURE_MS = 15000;

// Fontes do token, em ordem (GEN-3111):
//  1) cookie (primário)
//  2) localStorage — cobre navegadores que bloqueiam cookies por site
//  3) store em memória (zustand) — cobre ambientes corporativos que bloqueiam
//     TODO o armazenamento do site (cookie E localStorage); sobrevive à navegação
//     interna /login -> / (sem reload). Caso Bradesco: storage bloqueado na origem
//     da plataforma. Só se perde num reload de página (aí o SSO reautentica).
// GEN-4137: exportada para o Kanban 2.0 (getToken do mountKanbanV2) usar a MESMA cadeia — antes
// ele só lia o cookie e, sem cookie, chamava a API sem Bearer (403 "Forbidden resource").
export const readSessionToken = (): string =>
  getCookie("token") || readSessionFallback() || useLoginToken.getState().token || "";

export const ApiAuth = (add: object | undefined = undefined) => {
  const token = readSessionToken();

  // GEN-3199: em ambiente onde o agente corporativo bloqueia requisições com
  // `Authorization`, omitimos o header — o cookie `token` (mesma origem) chega ao
  // proxy nginx, que reconstrói o header para o backend. Ver authHeaderFallback.
  // Tipo único (Record) em ambos os ramos: uma união quebraria quem lê
  // `.headers.Authorization`. A ordem preserva o contrato original — o que vem em
  // `add` sobrescreve o Authorization.
  const extra = (add ?? {}) as Record<string, string>;
  const headers: Record<string, string> = isAuthHeaderBlocked()
    ? { ...extra }
    : { Authorization: `Bearer ${token}`, ...extra };

  return {
    headers,
    timeout: 1200000,
  };
};

/**
 * GEN-3191: marca uma requisição como NÃO repetível pelo fallback do GEN-3199.
 *
 * O fallback repete a chamada sem `Authorization` quando ela falha rápido e sem resposta HTTP.
 * Isso é seguro para leitura e para escrita idempotente, mas não para um POST que CRIA entidade:
 * se o servidor aceitou o request e a resposta se perdeu na volta, o replay cria a entidade de
 * novo — e o caller fica só com o id do segundo. Some-se que a chamada mais pesada do produto é
 * também a mais sujeita a queda de rede dentro da janela de 15s, o que faria uma falha comum ser
 * lida como "agente corporativo bloqueando o header" e colocaria a SESSÃO INTEIRA no modo sem
 * header (`markAuthHeaderBlocked`).
 */
export const noAuthRetry = <T extends object>(config: T): T & { __taxlyNoAuthRetry: true } => ({
  ...config,
  __taxlyNoAuthRetry: true,
});

// GEN-3223 — 403 do backend quando a sessão não tem concessão de 2FA da Caixa Postal, e o evento de
// janela que pede à tela do módulo para reabrir o modal do código. Moram em `twoFactorRequired.ts`
// porque o `useToast` também escuta (e não deve importar o cliente HTTP).
export { DTE_2FA_REQUIRED, TWO_FACTOR_REQUIRED_EVENT } from "./twoFactorRequired";

export class ApiCall {
  base = process.env.REACT_APP_TAXLY_API;
  currentRoute: string | null = null;
  currentConfig: any | undefined = undefined;
  cancelToken: undefined | CancelToken;

  /**
   * Start an API call
   * @param route The route
   * @returns
   * @example
   * await new ApiCall(route).post<ResponseInterface,InputInterface>(data)
   * //**ResponseInterface** = Response Interface (The object that returns from the Backend, inside the "response" Object)
   * //**InputInterface** = Input Interface (Object that is being passed to the system)
   */
  constructor(route: string, config: AxiosRequestConfig | undefined = undefined, cancelToken?: CancelToken) {
    this.currentRoute = route;
    this.currentConfig = config;
    this.cancelToken = cancelToken;
    return this;
  }

  /**
   * GEN-3199 — detecção do bloqueio do header `Authorization`.
   *
   * Se a requisição falhar SEM resposta HTTP (o agente corporativo bloqueia a
   * chamada antes de sair da máquina) e ela levava `Authorization`, registra o
   * bloqueio e repete UMA vez sem o header: o cookie `token` + o proxy nginx
   * cobrem a autenticação. A partir daí, `ApiAuth` já não envia o header, então
   * apenas a primeira chamada paga o custo do retry.
   *
   * Distinguir bloqueio de TIMEOUT por `err.code` não funcionou: no teste real
   * (Bradesco, 30/07) o retry nunca disparou — a telemetria voltou com
   * `authHeaderBlocked:false` — porque uma conexão abortada pelo agente também
   * chega como `ECONNABORTED`, que era descartado como timeout.
   * Passamos a distinguir por TEMPO, que independe do código reportado pelo
   * navegador: o timeout configurado é de 300s, enquanto o bloqueio falha em
   * milissegundos (2ms a 150ms nos casos observados).
   *
   * Só age quando a API é de mesma origem — sem proxy não há quem derive o header.
   */
  private withAuthHeaderFallback(instance: AxiosInstance): AxiosInstance {
    instance.interceptors.request.use((config) => {
      (config as AxiosRequestConfig & { __taxlyT0?: number }).__taxlyT0 = Date.now();
      return config;
    });

    instance.interceptors.response.use(undefined, async (err: AxiosError) => {
      const config = err.config as
        | (AxiosRequestConfig & { __taxlyAuthRetry?: boolean; __taxlyT0?: number; __taxlyNoAuthRetry?: boolean })
        | undefined;
      const headers = (config?.headers ?? {}) as Record<string, unknown>;

      // Falha rápida (bem abaixo do timeout de 300s) = a requisição não chegou a
      // trafegar; é bloqueio local, não lentidão de rede.
      const elapsed = config?.__taxlyT0 ? Date.now() - config.__taxlyT0 : 0;
      const failedFast = elapsed < FAST_FAILURE_MS;
      const blockedBeforeSending = !err.response && !axios.isCancel(err) && failedFast;

      // GEN-3191: `noAuthRetry()` exclui a chamada do replay. Repetir só é seguro quando a
      // requisição é idempotente — ver o helper para o porquê.
      if (
        blockedBeforeSending &&
        config &&
        !config.__taxlyAuthRetry &&
        !config.__taxlyNoAuthRetry &&
        headers.Authorization &&
        isSameOriginApi()
      ) {
        markAuthHeaderBlocked();
        config.__taxlyAuthRetry = true;
        delete headers.Authorization;
        return instance.request(config);
      }

      return Promise.reject(err);
    });

    return instance;
  }

  private caller(): AxiosInstance {
    return this.withAuthHeaderFallback(axios.create({ baseURL: this.base, timeout: 300000 }));
  }

  private blob(): AxiosInstance {
    return this.withAuthHeaderFallback(axios.create({ baseURL: this.base, timeout: 300000, responseType: "blob" }));
  }

  /**
   * GEN-3604 (F0.2): GET autenticado devolvendo o binário. Serve para abrir um arquivo do `/file/get`
   * mandando a sessão no header — `window.open` numa URL montada no navegador não leva `Authorization`
   * nem ticket, e passa a ser recusado quando `FILE_GET_REQUIRE_TICKET` ligar no backend.
   */
  async getBlob(): Promise<Blob | null> {
    try {
      const data = await this.blob().get(this.currentRoute as string, this.currentConfig);
      return data.data as Blob;
    } catch (err: any) {
      await this.decodeBlobError(err);
      this.handleErrorResponse(err);
      return null;
    }
  }

  async download<InputInterface>(obj?: InputInterface | undefined): Promise<any> {
    try {
      const data = await this.blob().post(this.currentRoute as string, obj, this.currentConfig);
      return data.data;
    } catch (err: any) {
      await this.decodeBlobError(err);
      this.handleErrorResponse(err);
      return err;
    }
  }

  /**
   * GEN-3604: com `responseType: "blob"` o axios entrega TAMBÉM o corpo de erro como `Blob` — o JSON
   * do backend (`{ response: { message: "AUTH_USER_BLOCKED" } }`) chega opaco, `handleErrorResponse`
   * lê `data.response.message` como `undefined` e a conta bloqueada/desativada (ou a sessão expirada)
   * seguia na tela sem redirect nem limpeza de sessão. Aqui o Blob JSON é decodificado de volta para
   * objeto antes de o tratamento comum rodar. Só mexe quando o tipo é JSON (ou desconhecido); um
   * binário de verdade ou um HTML de proxy passam intactos. Falha na decodificação não é erro novo:
   * o objeto fica como veio e o fluxo degrada como antes.
   */
  private decodeBlobError = async (err: any): Promise<void> => {
    const data = err?.response?.data;
    if (typeof Blob === "undefined" || !(data instanceof Blob)) return;
    if (data.type && !/json/i.test(data.type)) return;
    try {
      const text = typeof data.text === "function" ? await data.text() : await this.readBlobAsText(data);
      err.response.data = JSON.parse(text);
    } catch {
      // Corpo não era JSON legível: mantém o Blob e deixa o tratamento comum seguir.
    }
  };

  /** `Blob.text()` não existe em todo runtime (jsdom antigo, WebView); FileReader é o caminho universal. */
  private readBlobAsText = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });

  /**
   * Post Request
   * @param obj
   * @returns
   */
  async post<ResponseInterface, InputInterface>(
    obj?: InputInterface | undefined
  ): Promise<TPremiseResponse<ResponseInterface>> {
    try {
      const data = await this.caller().post(this.currentRoute as string, obj, {
        ...this.currentConfig,
        cancelToken: this.cancelToken,
      });
      return data.data as IDefaultResponse<ResponseInterface>;
    } catch (err: any) {
      if (axios.isCancel(err)) {
        const error = {
          response: (err as AxiosError).response?.data,
          status: null,
        } as IDefaultResponse<IErrorResponse>;
        return error;
      }

      if (!axios.isCancel(err)) {
        this.handleErrorResponse(err);
      }

      return this.buildErrorResponse(err);
    }
  }

  /**
   * Patch Request
   * @param obj
   * @returns
   */
  async patch<ResponseInterface, InputInterface>(
    obj?: InputInterface | undefined
  ): Promise<TPremiseResponse<ResponseInterface>> {
    try {
      const data = await this.caller().patch(this.currentRoute as string, obj, this.currentConfig);
      return data.data as IDefaultResponse<ResponseInterface>;
    } catch (err: any) {
      this.handleErrorResponse(err);
      return this.buildErrorResponse(err);
    }
  }

  /**
   * Put Request
   * @param obj
   * @returns
   */
  async put<ResponseInterface, InputInterface>(
    obj?: InputInterface | undefined
  ): Promise<TPremiseResponse<ResponseInterface>> {
    try {
      const data = await this.caller().put(this.currentRoute as string, obj, this.currentConfig);
      return data.data as IDefaultResponse<ResponseInterface>;
    } catch (err: any) {
      this.handleErrorResponse(err);
      return this.buildErrorResponse(err);
    }
  }

  /**
   * Get Request
   * @returns
   */
  async get<ResponseInterface>(): Promise<TPremiseResponse<ResponseInterface>> {
    try {
      const data = await this.caller().get(this.currentRoute as string, {
        ...this.currentConfig,
        cancelToken: this.cancelToken,
      });
      return data.data as IDefaultResponse<ResponseInterface>;
    } catch (err: any) {
      if (axios.isCancel(err)) {
        const error = {
          response: (err as AxiosError).response?.data,
          status: null,
        } as IDefaultResponse<IErrorResponse>;
        return error;
      }

      if (!axios.isCancel(err)) {
        this.handleErrorResponse(err);
      }

      return this.buildErrorResponse(err);
    }
  }

  async delete<ResponseInterface>(): Promise<TPremiseResponse<ResponseInterface>> {
    try {
      const data = await this.caller().delete(this.currentRoute as string, this.currentConfig);
      return data.data as IDefaultResponse<ResponseInterface>;
    } catch (err: any) {
      this.handleErrorResponse(err);
      return this.buildErrorResponse(err);
    }
  }

  /**
   * Garante que o catch sempre retorne um objeto `{ status, response }` válido.
   * Quando a request falha sem resposta HTTP (erro de rede, timeout, abort),
   * `err.response` é undefined e o retorno cru quebraria a desestruturação do caller.
   */
  private buildErrorResponse = (err: any): IDefaultResponse<IErrorResponse> => {
    const axiosErr = err as AxiosError;
    const data = axiosErr.response?.data as IDefaultResponse<IErrorResponse> | undefined;

    if (data) {
      return data;
    }

    return {
      status: axiosErr.response?.status ?? null,
      requestTime: "",
      response: {
        service: this.currentRoute ?? "",
        method: "",
        // GEN-3199: inclui o code do axios (ERR_NETWORK, ECONNABORTED, ...) —
        // sem ele, uma falha sem status HTTP não diz por que aconteceu.
        message: [axiosErr.code, axiosErr.message].filter(Boolean).join(": ") || "REQUEST_FAILED",
      },
    };
  };

  private handleErrorResponse = (err: any) => {
    const message = err.response?.data?.response?.message;

    // GEN-3224: o backend passou a negar a conta bloqueada/desativada no próprio caminho de
    // autenticação (403 com código próprio). É diferente de sessão expirada: o usuário não deve
    // ver "faça login novamente" e sim o motivo real — logar de novo não vai resolver.
    if (err.response?.status === 403 && ["AUTH_USER_BLOCKED", "AUTH_USER_INACTIVE"].includes(message)) {
      // GEN-3604: no portal a saída é pelo login DO PORTAL, com a sessão limpa — `/logout` é tela
      // da plataforma e, no subdomínio, "logout" viraria o slug de um cliente.
      if (this.inPortal()) {
        this.leavePortal(message === "AUTH_USER_BLOCKED" ? "blocked" : "inactive");
        return;
      }
      window.location.href = message === "AUTH_USER_BLOCKED" ? "/logout?blocked=1" : "/logout?inactive=1";
      return;
    }

    // GEN-3604: quem só tem acesso ao portal e abre uma tela da plataforma (link antigo, favorito, URL
    // digitada) recebe 403 PORTAL_ONLY_USER do backend em toda rota fora do portal. O lugar dessa pessoa
    // é o portal, com a sessão que ela já tem — não o logout.
    if (err.response?.status === 403 && message === "PORTAL_ONLY_USER") {
      if (!this.inPortal()) window.location.href = "/portal";
      return;
    }

    // GEN-3223: o 2FA da Caixa Postal passou a valer no servidor, por sessão e com prazo. Quando a
    // concessão vence no meio do uso, QUALQUER chamada de /dte volta com este 403 — avisa a tela, que
    // reabre o modal do código. Cada chamada continua recebendo o próprio erro.
    if (err.response?.status === 403 && message === DTE_2FA_REQUIRED) {
      pedirTwoFactorDeNovo("dte");
      return;
    }

    if (err.response?.status === 401) {
      if (["LOGIN_USER_BLOCKED", "LOGIN_ACCOUNT_IS_BLOCKED", "LOGIN_WRONG_PASSWORD"].includes(message)) {
        return;
      }

      // GEN-3167: a sessão caiu com o usuário no meio de alguma tela. Este é um redirect de página
      // inteira, então o guard do AppLayout não chega a rodar — o destino tem que ser guardado
      // aqui, senão o login seguinte devolve o Dashboard em vez da tela onde ele estava. É o
      // caminho de QUALQUER 401 autenticado do produto, não só o do módulo de atividades.
      // GEN-3604: dentro do portal NÃO se guarda destino de retorno — senão o próximo login na
      // plataforma (ex.: o admin do cliente) é jogado para o portal em vez do destino que ele pediu.
      if (this.inPortal()) {
        // GEN-3604: quem está no Portal de Autosserviço volta para o login DO PORTAL, não para o
        // fluxo da plataforma — é outro público, que nunca viu o Dashboard.
        this.leavePortal("expired");
        return;
      }
      rememberReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      window.location.href = "/logout?expired=1";
    }
  };

  /** Dentro do Portal de Autosserviço: pelo caminho `/portal/*` na plataforma ou pelo host próprio. */
  private inPortal = (): boolean =>
    (window.location.pathname ?? "").startsWith("/portal") || isPortalHost(window.location.hostname);

  /**
   * GEN-3604: sair do portal APAGANDO a sessão que o backend acabou de rejeitar. O
   * `/logout` da plataforma faz isso antes de mandar para o login; o portal ia direto para o login
   * com cookie e fallback intactos — e o login do portal, vendo um token, voltava para a home, que
   * levava 401 de novo: um loop de reload contra o backend, sem nunca mostrar "sua sessão expirou".
   * O cookie é de sessão e gravado com `path=/` (mesmas opções do login do portal).
   */
  private leavePortal = (reason: "expired" | "blocked" | "inactive") => {
    clearSessionToken();
    setCookie("token", "", { days: -1, path: "/" });
    useLoginToken.getState().setToken(null);
    // No host próprio do portal as rotas ficam na raiz (`/login`) e um slug de cliente pode começar
    // com "portal" (ex.: `/portal-cliente`) — por isso quem decide é o HOST, não o caminho.
    const login = isPortalHost(window.location.hostname) ? "/login" : "/portal/login";
    window.location.href = `${login}?${reason}=1`;
  };
}
